/**
 * OperationDetailPage.js — Vista de detalle de una operación de remesa.
 *
 * Visible para CEO, gestor que la creó y proveedor CU asignado. Cada rol
 * ve la información financiera filtrada:
 *   - CEO: ve ganancia bruta + las 3 shares + costos.
 *   - Gestor: ve su 25% y la ganancia bruta sin desglose detallado.
 *   - Proveedor CU: ve su 50% + datos USDT (cost/sale/volumen).
 *
 * Mounted at /operations/:id (shared across role layouts).
 */
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Send, CheckCircle, Clock, AlertTriangle, Wallet, TrendingUp, Share2, MessageCircle } from 'lucide-react';
import StateBadge from '@/components/workflow/StateBadge';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD = (label, value, mono = false) => (
  <div>
    <p className="text-[10px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`text-sm text-slate-900 ${mono ? 'font-mono' : ''}`}>{value ?? '—'}</p>
  </div>
);

const fmt = (n, ccy) =>
  n == null ? '—' : `${Number(n).toLocaleString('es-ES', { minimumFractionDigits: 2 })} ${ccy || ''}`.trim();

const OperationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [op, setOp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/operations/${id}`, { headers: getAuthHeaders() });
      setOp(r.data);
    } catch (e) {
      toast.error('No se pudo cargar la operación');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const transition = async (target_state) => {
    setTransitioning(true);
    try {
      const r = await axios.post(`${API}/operations/${id}/transition`, { target_state }, { headers: getAuthHeaders() });
      setOp(r.data);
      toast.success(`Estado actualizado: ${target_state}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo transicionar');
    } finally {
      setTransitioning(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-slate-400">Cargando…</div>;
  if (!op) return <div className="p-10 text-center text-rose-600">Operación no encontrada</div>;

  const md = op.metadata || {};
  const role = user?.role;
  const isAdmin = role === 'admin';
  const isProvider = role === 'payment_agent';
  const isGestor = role === 'gestor';

  // Provider quick-action buttons (only when this is their op).
  const showProcessing = isProvider && op.state === 'ASSIGNED_TO_PROVIDER';
  const showDeliver = isProvider && (op.state === 'PROCESSING' || op.state === 'ASSIGNED_TO_PROVIDER');

  // WhatsApp helpers: build the receipt message + click handler.
  const phone = (md.beneficiary_phone || md.client_phone || '').replace(/[^\d+]/g, '');
  const hasPhone = phone.length >= 6;
  const buildReceiptMessage = () => {
    const ar = md.amount_received || md.recibe_aproximado || 0;
    const fa = md.funding_amount || op.amount || 0;
    const fc = md.funding_currency || op.currency_from || 'UYU';
    const lines = [
      '✅ *Remesa entregada — Aircom Connect*',
      '',
      `📌 Código: ${op.id.slice(0, 8).toUpperCase()}`,
      `💸 Enviado: ${Number(fa).toLocaleString('es-ES', { minimumFractionDigits: 2 })} ${fc}`,
      `💰 Recibido: ${Number(ar).toLocaleString('es-ES', { minimumFractionDigits: 2 })} ${op.currency_to}`,
    ];
    if (md.card_last4) lines.push(`💳 Destinatario: **** ${md.card_last4}`);
    if (md.beneficiary) lines.push(`👤 Beneficiario: ${md.beneficiary}`);
    lines.push('', '✈️ _Aircom Connect — Conectamos tus sueños con el mundo_');
    return lines.join('\n');
  };
  const openWhatsApp = () => {
    const msg = encodeURIComponent(buildReceiptMessage());
    const cleanPhone = phone.replace(/^\+/, '');
    const url = hasPhone ? `https://wa.me/${cleanPhone}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="operation-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Send className="w-5 h-5 text-amber-600" /> Remesa {op.id.slice(0, 8)}…
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">Creada el {new Date(op.created_at).toLocaleString('es-ES')}</p>
        </div>
        {/* Compartir por WhatsApp — sólo cuando la remesa fue entregada */}
        {op.state === 'DELIVERED' && (
          <Button
            onClick={openWhatsApp}
            className="bg-[#25D366] hover:bg-[#1ebe5d] text-white"
            data-testid="share-whatsapp-btn"
            title={hasPhone ? `Enviar comprobante a ${phone}` : 'Compartir comprobante por WhatsApp'}
          >
            <MessageCircle className="w-4 h-4 mr-1" />
            {hasPhone ? 'Enviar comprobante' : 'Compartir'}
          </Button>
        )}
        <StateBadge state={op.state} />
      </div>

      {/* Quick actions for provider CU */}
      {(showProcessing || showDeliver) && (
        <Card className="p-4 bg-emerald-50 border-emerald-200 flex items-center justify-between" data-testid="provider-actions">
          <p className="text-sm text-emerald-800">Acciones disponibles para vos como proveedor:</p>
          <div className="flex gap-2">
            {showProcessing && (
              <Button onClick={() => transition('PROCESSING')} disabled={transitioning} className="bg-amber-600 hover:bg-amber-700" data-testid="btn-processing">
                <Clock className="w-4 h-4 mr-1" /> Empezar
              </Button>
            )}
            {showDeliver && (
              <Button onClick={() => transition('DELIVERED')} disabled={transitioning} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-deliver">
                <CheckCircle className="w-4 h-4 mr-1" /> Marcar Entregada
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Datos generales — visibles para todos */}
      <Card className="p-5 border-0 shadow-sm">
        <h3 className="font-bold text-slate-900 mb-4">Datos de la remesa</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {FIELD('Monto enviado', fmt(md.funding_amount || op.amount, md.funding_currency || op.currency_from), true)}
          {FIELD('Moneda destino', op.currency_to)}
          {FIELD('Beneficiario recibe', fmt(md.monto_destino || md.amount_received || md.recibe_aproximado, op.currency_to), true)}
          {FIELD('Tarjeta (últimos 4)', md.card_last4 ? `**** ${md.card_last4}` : '—', true)}
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-500">Teléfono</p>
            {hasPhone ? (
              <a
                href={`https://wa.me/${phone.replace(/^\+/, '')}?text=${encodeURIComponent(buildReceiptMessage())}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-emerald-600 hover:text-emerald-700 hover:underline font-medium inline-flex items-center gap-1"
                data-testid="phone-whatsapp-link"
                title="Abrir WhatsApp con el comprobante"
              >
                {md.beneficiary_phone || md.client_phone}
                <MessageCircle className="w-3.5 h-3.5" />
              </a>
            ) : (
              <p className="text-sm text-slate-900">—</p>
            )}
          </div>
          {FIELD('País destino', md.target_country || '—')}
          {FIELD('Modo', md.is_quick ? 'Rápido' : 'Avanzado')}
          {FIELD('Tasa locked', (md.tasa_cliente || md.rate_locked) ? `${Number(md.tasa_cliente || md.rate_locked).toFixed(4)}` : '—', true)}
          {FIELD('Funding', md.funding_mode === 'admin_direct' ? 'CEO directo' : md.funding_mode === 'gestor_balance' ? 'Saldo gestor' : (md.funding_mode || '—'))}
        </div>

        {/* Iter 64 · Quien envía — visible para CEO y Proveedor CU.
            Permite saber sin abrir otra vista quién originó la operación. */}
        {(isAdmin || isProvider) && (op.gestor_info || op.created_by_info) && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-2">
              Enviada por
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <p className="text-slate-900 font-semibold" data-testid="op-sender-name">
                {(op.created_by_info?.name || op.gestor_info?.name) || '—'}
                <span className="ml-2 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold">
                  {op.created_by_info?.role || (op.gestor_id ? 'gestor' : '—')}
                </span>
              </p>
              {(op.gestor_info?.email || op.created_by_info?.email) && (
                <p className="text-slate-500" data-testid="op-sender-email">
                  📧 {op.gestor_info?.email || op.created_by_info?.email}
                </p>
              )}
              {op.gestor_info?.phone && (
                <p className="text-slate-500" data-testid="op-sender-phone">
                  📱 {op.gestor_info.phone}
                </p>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Información económica — role-aware (Iter 64 · canónico USDT) */}
      {(() => {
        // Snapshot Iter 62 canónico — prioridad sobre legacy v3 UYU.
        const usdtComprados = md.usdt_comprados ?? md.usdt_volume ?? null;
        const usdtConsumidos = md.usdt_consumidos ?? md.usdt_sale_uyu ?? null;
        const gananciaTotal = md.ganancia_total ?? md.gross_profit_usdt ?? null;
        const shareProv = md.ganancia_proveedor ?? md.share_provider_usdt ?? md.share_provider_uyu ?? null;
        const shareCeo  = md.ganancia_ceo       ?? md.share_ceo_usdt      ?? md.share_ceo_uyu      ?? null;
        const shareGest = md.ganancia_gestor    ?? md.share_gestor_usdt   ?? md.share_gestor_uyu   ?? null;
        const deudaProv = md.deuda_proveedor    ?? null;
        const tasaCompra = md.tasa_compra_usdt  ?? null;
        const precioProv = md.precio_proveedor  ?? md.bridge_leg2_rate ?? null;
        const hasEconomics = (usdtComprados != null || usdtConsumidos != null || gananciaTotal != null);
        // Para el gestor, alias "USDT" → "USD".
        const assetLabel = isGestor ? 'USD' : 'USDT';
        const fmt6 = (n) => n == null ? '—' : `${Number(n).toLocaleString('es-ES', { minimumFractionDigits: 4, maximumFractionDigits: 6 })} ${assetLabel}`;

        if (!hasEconomics) {
          return (
            <Card className="p-5 border-0 shadow-sm bg-slate-50 text-sm text-slate-500 flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              La distribución de ganancia (50/25/25) se calcula cuando el proveedor CU marca la remesa como entregada.
            </Card>
          );
        }

        return (
          <Card className="p-5 border-0 shadow-sm bg-gradient-to-br from-slate-50 to-emerald-50">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Información económica
              <Badge variant="outline" className="text-[10px]">v5 · split 50/25/25</Badge>
              {!md.v3_distributed && (
                <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">
                  pendiente distribución
                </Badge>
              )}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Volumen + costos USDT — CEO y Proveedor */}
              {(isAdmin || isProvider) && FIELD(`${assetLabel} comprado`, fmt6(usdtComprados), true)}
              {(isAdmin || isProvider) && FIELD(`${assetLabel} vendido`, fmt6(usdtConsumidos), true)}
              {(isAdmin || isProvider) && FIELD('Precio compra (UYU/' + assetLabel + ')', tasaCompra ? Number(tasaCompra).toLocaleString('es-ES', { minimumFractionDigits: 2 }) : '—', true)}
              {(isAdmin || isProvider) && FIELD('Precio venta (' + assetLabel + '/' + op.currency_to + ')', precioProv ? Number(precioProv).toLocaleString('es-ES', { minimumFractionDigits: 2 }) : '—', true)}
              {(isAdmin || isProvider) && FIELD('Ganancia bruta', fmt6(gananciaTotal), true)}
              {(isAdmin || isProvider) && FIELD('Deuda al proveedor', fmt6(deudaProv), true)}

              {/* Shares — Iter 62 ya están en USDT. El gestor solo ve la suya. */}
              {isAdmin && (
                <div className="md:col-span-4 mt-2 p-3 bg-white rounded-lg border">
                  <p className="text-[10px] uppercase text-slate-500">Distribución (todas)</p>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div>
                      <p className="text-[10px] text-slate-400">Proveedor 50%</p>
                      <p className="text-sm font-mono font-bold text-blue-700">{fmt6(shareProv)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400">CEO 25%</p>
                      <p className="text-sm font-mono font-bold text-yellow-700">{fmt6(shareCeo)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400">Gestor 25%</p>
                      <p className="text-sm font-mono font-bold text-emerald-700">{fmt6(shareGest)}</p>
                    </div>
                  </div>
                </div>
              )}
              {isProvider && (
                <div className="md:col-span-2 mt-2 p-3 bg-white rounded-lg border-l-4 border-blue-500">
                  <p className="text-[10px] uppercase text-slate-500">Tu ganancia (50%)</p>
                  <p className="text-2xl font-bold font-mono text-blue-700">{fmt6(shareProv)}</p>
                </div>
              )}
              {isGestor && (
                <div className="md:col-span-2 mt-2 p-3 bg-white rounded-lg border-l-4 border-emerald-500">
                  <p className="text-[10px] uppercase text-slate-500">Tu ganancia</p>
                  <p className="text-2xl font-bold font-mono text-emerald-700">{fmt6(shareGest)}</p>
                </div>
              )}
            </div>
          </Card>
        );
      })()}

      {/* Timeline de estados */}
      {Array.isArray(op.history) && op.history.length > 0 && (
        <Card className="p-5 border-0 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-3">Historial de estados</h3>
          <ol className="relative border-l-2 border-slate-200 ml-3 space-y-3">
            {op.history.map((h, i) => (
              <li key={`${h.at || 'unknown'}-${h.to_state || 'x'}-${i}`} className="ml-4">
                <div className="absolute -left-1.5 w-3 h-3 rounded-full bg-emerald-500 mt-1.5" />
                <p className="text-xs text-slate-500">{new Date(h.at).toLocaleString('es-ES')}</p>
                <p className="text-sm text-slate-900">
                  <StateBadge state={h.to_state} />
                  <span className="text-xs text-slate-400 ml-2">{h.by_role || ''}</span>
                </p>
                {h.note && <p className="text-xs text-slate-500 italic">{h.note}</p>}
              </li>
            ))}
          </ol>
        </Card>
      )}

      {!md.v3_distributed && op.state === 'CREATED' && (
        <Card className="p-4 bg-amber-50 border-amber-200 flex items-center gap-2 text-sm text-amber-800">
          <AlertTriangle className="w-4 h-4" /> Esta remesa todavía no fue asignada al proveedor.
        </Card>
      )}
    </div>
  );
};

export default OperationDetailPage;
